import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PageFilterProvider } from './contexts/PageFilterContext';
import { AchievementProvider } from './components/shared/AchievementSystem';
import { GlobalSearch } from './components/shared/GlobalSearch';
import { FloatingDepositButton } from './components/shared/AchievementSystem';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import EmployeeApp from './pages/EmployeeApp';
import PlayersPage from './pages/PlayersPage';
import BattleLogPage from './pages/BattleLogPage';
import EmployeeIntelPage from './pages/EmployeeIntelPage';
import WalletPage from './pages/WalletPage';
import ArenasPage from './pages/ArenasPage';
import GuildMembersPage from './pages/GuildMembersPage';
import PerformanceReportsPage from './pages/PerformanceReportsPage';
import SystemResetPage from './pages/SystemResetPage';
import AdminManagementPage from './pages/AdminManagementPage';
import './index.css';

const ADMIN_ROLES = ['admin', 'super_admin'];

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
}

// Global Search and Floating Button wrapper
function GlobalFeatures() {
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);

  // Global keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!user) return null;

  const handleDepositClick = () => {
    window.location.href = '/wallet';
  };

  return (
    <>
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      {user.role === 'admin' && <DepositButtonWrapper onClick={handleDepositClick} />}
      {user.role === 'super_admin' && <DepositButtonWrapper onClick={handleDepositClick} />}
    </>
  );
}

function DepositButtonWrapper({ onClick }) {
  const location = useLocation();
  return <FloatingDepositButton onClick={onClick} />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      
      <Route
        path="/"
        element={
          <PrivateRoute>
            {ADMIN_ROLES.includes(user?.role) ? <AdminDashboard /> : <EmployeeApp />}
          </PrivateRoute>
        }
      />

      {/* Direct employee app route - accessible to both roles for testing */}
      <Route
        path="/employee"
        element={
          <PrivateRoute>
            <EmployeeApp />
          </PrivateRoute>
        }
      />

      <Route
        path="/players"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <PlayersPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <PrivateRoute>
            <BattleLogPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <EmployeeIntelPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/wallet"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <WalletPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/games"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <ArenasPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/employees"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <GuildMembersPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/reports/performance"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <PerformanceReportsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/admin/system-reset"
        element={
          <PrivateRoute roles={ADMIN_ROLES}>
            <SystemResetPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/admin/manage"
        element={
          <PrivateRoute roles={['super_admin']}>
            <AdminManagementPage />
          </PrivateRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <AchievementProvider>
            <PageFilterProvider>
              <BrowserRouter>
                <AppRoutes />
                <GlobalFeatures />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </PageFilterProvider>
          </AchievementProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;